import { useNavigate } from "react-router-dom";
import MainContainer from "../../layout/MainContainer";
import CategoryList from "../../features/manageCategories/categoryList";
import CategoryTopbar from "../../features/manageCategories/topbar";
import { useEffect, useState } from "react";
import {
  useAddCategoryMutation,
  useUpdateCategoryMutation,
  useLazyDeleteCategoryQuery,
  useLazyGetCategoryQuery,
} from "../../services/category";
import { showError, showToast, showWarning } from "../../constants/toasts";
import { UploadMedia } from "../../utils/mediaUpload";
import { ConfirmationModal } from "../../Modals/confirmationModal";
import CategoryDetails from "../../Modals/categoryDetails";
import { generateEncryptedKeyBody } from "../../utils/crypto";
import { CommonBody } from "../../types/General";
import usePermission from "../../hooks/usePermission";
import useTranslation from "../../hooks/traslation";
import Loader from "../../constants/Loader";

// interface CategoryData {
//   name: string;
//   image: string;
//   startPrice: number;
// }

const ManageCategories = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [addCategory] = useAddCategoryMutation();
  const [updateCateogery] = useUpdateCategoryMutation();
  const [deleteCategory] = useLazyDeleteCategoryQuery();
  const [getCategory] = useLazyGetCategoryQuery();
  const [selectedId, setSelectedId] = useState<string>("");
  const [error, setError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isStatusLoading, setIsStatusLoading] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [openDetails, setOpenDetails] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [isFormLoading, setIsFormLoading] = useState(false);

  const [image, setImage] = useState<string>("");
  const [price, setPrice] = useState<string>("");
  const [name, setName] = useState<string>("");
  const [nameAr, setNameAr] = useState<string>("");
  const [descriptionAr, setDescriptionAr] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const { isAdd, isDelete } = usePermission({
    label: "Manage Categories",
    page_permission: "isView",
  });

  const [rows, setRows] = useState<{ _id: string; status?: boolean }[]>([]);

  const [page, setPage] = useState<number>(1);
  const [totalCount, setTotalCount] = useState<number>(10);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState("");

  const translate = useTranslation();

  const handlePageChange = (event: any, newPage: number) => {
    setPage(newPage + 1);
  };
  const getAllCategory = async () => {
    setIsLoading(true);
    setIsPageLoading(true);
    try {
      const response = await getCategory({
        page,
        limit: 10,
        search: debouncedSearchTerm.trim(),
      }).unwrap();
      if (response?.statusCode === 200) {
        setTotalCount(response?.data?.totalCount);
        setRows(
          response?.data?.list?.map((data: { _id: string }) => ({
            ...data,
            id: data?._id,
          })) || []
        );
      }
    } catch (error) {
      showError(translate?.auth?.failedToLoad);
    } finally {
      setIsLoading(false);
      setIsPageLoading(false);
    }
  };

  const handleSubmit = async () => {
    setError(true);
    setIsFormLoading(true);
    const body = {
      name: name,
      name_ar: nameAr,
      image: image,
      ...(description ? { description: description } : {}),
      ...(descriptionAr ? { description_ar: descriptionAr } : {}),
    };

    if (selectedId) {
      try {
        const response = await updateCateogery({
          body,
          id: selectedId,
        }).unwrap();
        if (response?.statusCode === 200) {
          getAllCategory();
          showToast(translate?.auth?.updatedCategory);
          setOpen(false);
        }
      } catch (error: any) {
        console.log("error: ", error);
        showError(error?.data?.message);
      } finally {
        setIsFormLoading(false);
      }
    } else {
      if (image.length === 0) {
        showWarning(translate?.auth?.requiredImage);
        setIsFormLoading(false);
        return;
      }
      try {
        const response = await addCategory({ data: body }).unwrap();
        if (response?.statusCode === 200) {
          showToast(translate?.auth?.addedCategory);
          getAllCategory();
          setOpen(false);
          setName("");
          setPrice("");
          setImage("");
          setDescription("");
          setDescriptionAr("");
          setNameAr("");
        }
      } catch (error: any) {
        showError(error?.data?.message);
      } finally {
        setIsFormLoading(false);
        getAllCategory();
      }
    }
  };

  const handleProfileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    const file = files?.length ? files[0] : null;

    if (!file) {
      setImage("");
      return;
    }
    if (
      file &&
      file.type &&
      file.type !== "image/jpeg" &&
      file.type !== "image/jpg" &&
      file.type !== "image/png"
    ) {
      showError(translate?.auth?.imageFormat);
      return;
    }

    const imageUrl = (await UploadMedia(file)) as any;
    if (imageUrl?.data) {
      setImage(imageUrl?.data?.image || "");
    }
  };

  const handleDelete = async () => {
    setIsLoading(true);
    try {
      const response = await deleteCategory({ id: selectedId }).unwrap();
      setIsLoading(false);

      if (response?.statusCode === 200) {
        showToast(translate?.auth?.deleteCategory);
        if (rows.length === 1 && page > 1) {
          setPage(page - 1);
        } else {
          getAllCategory();
        }
        getAllCategory();
      }
    } catch (error: any) {
      showError(error?.data?.message || "");
      setIsLoading(false);
    }
  };

  const handleStatusChange = async (id: string, status: boolean) => {
    setIsStatusLoading(true);
    try {
      const body = {
        status: status,
      };
      const response = await updateCateogery({
        id: id,
        body: body,
      }).unwrap();

      if (response.statusCode === 200) {
        showToast(
          status
            ? translate?.auth?.enabledCategory
            : translate?.auth?.disbaledCategory
        );
        setRows((prevRows) =>
          prevRows.map((row) => (row._id === id ? { ...row, status } : row))
        );
      }
    } catch (error: any) {
      showError(error?.data?.message || translate?.auth?.failToUpdate);
    } finally {
      setIsStatusLoading(false);
      getAllCategory();
    }
  };

  useEffect(() => {
    setIsPageLoading(true);
    getAllCategory();
  }, [page, debouncedSearchTerm]);
  const handleCloseModal = () => {
    setOpen(false);
  };
  return (
    <MainContainer isLoading={isPageLoading}>
      <div className="main_title">
        <h1>{translate?.main?.manageCategories}</h1>
        <p>
          <span onClick={() => navigate("/dashboard")}>
            {translate?.dashboard?.dashboard}
          </span>{" "}
          {translate?.main?.manageCategories}
        </p>
      </div>

      <div className="cards">
        <Loader isLoading={isFormLoading} />
        <CategoryTopbar
          name={name}
          setName={setName}
          price={price}
          setPrice={setPrice}
          handleSubmit={handleSubmit}
          setOpen={setOpen}
          open={open}
          image={image}
          setImage={setImage}
          selectedId={selectedId}
          setSelectedId={setSelectedId}
          handleProfileUpload={handleProfileUpload}
          setSearchTerm={setSearchTerm}
          setDebouncedSearchTerm={setDebouncedSearchTerm}
          searchTerm={searchTerm}
          setDescription={setDescription}
          description={description}
          setDescriptionAr={setDescriptionAr}
          descriptionAr={descriptionAr}
          setNameAr={setNameAr}
          nameAr={nameAr}
        />
        <CategoryList
          setSelctedId={setSelectedId}
          setOpen={setOpen}
          open={open}
          name={name}
          setName={setName}
          price={price}
          setPrice={setPrice}
          image={image}
          setImage={setImage}
          openDelete={openDelete}
          setOpenDelete={setOpenDelete}
          rows={rows}
          setRows={setRows}
          openDetails={openDetails}
          setOpenDetails={setOpenDetails}
          setSelectedId={setSelectedId}
          handleStatusChange={handleStatusChange}
          isLoading={isLoading}
          setDescription={setDescription}
          setDescriptionAr={setDescriptionAr}
          descriptionAr={descriptionAr}
          setNameAr={setNameAr}
          nameAr={nameAr}
          handlePageChange={handlePageChange}
          setPage={setPage}
          page={page}
          totalCount={totalCount}
        />
      </div>
      <ConfirmationModal
        text={translate?.auth?.confirmation}
        openModal={openDelete}
        setOpenModal={setOpenDelete}
        handleSubmit={handleDelete}
      />
      <CategoryDetails
        setOpen={setOpenDetails}
        open={openDetails}
        image={image}
        price={price}
        name={name}
        onClose={handleCloseModal}
        selectedId={selectedId}
        setSelectedId={setSelectedId}
        description={description}
        setDescriptionAr={setDescriptionAr}
        descriptionAr={descriptionAr}
        setNameAr={setNameAr}
        nameAr={nameAr}
      />
    </MainContainer>
  );
};

export default ManageCategories;
